import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://clvykdyagubzwcbnbhtw.supabase.co';

interface ELDVehicleLocation {
  vehicleId: string;
  vehicleName?: string;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  time: string;
}

interface ELDApiResponse {
  success: boolean;
  locations?: ELDVehicleLocation[];
  error?: string;
}

/**
 * Get ELD provider name from database
 */
async function getELDProvider(): Promise<string> {
  try {
    // eld_provider is a non-secret routing hint returned by integration-status,
    // so we don't read the locked system_settings table from the browser.
    const { data, error } = await supabase.functions.invoke('integration-status');
    if (error) return '';
    return data?.eldProvider || '';
  } catch (error) {
    console.error('Error getting ELD provider:', error);
    return '';
  }
}

/**
 * Call ELD API via Supabase Edge Function (to avoid CORS)
 * Routes to appropriate proxy based on provider
 */
async function callELDAPI(endpoint: string, method: string = 'GET', body?: any): Promise<any> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Not authenticated');
  }

  const provider = await getELDProvider();
  
  // Route to appropriate proxy based on provider
  const proxyEndpoint = provider === 'PRO_LOGS' 
    ? `${SUPABASE_URL}/functions/v1/prologs-proxy`
    : `${SUPABASE_URL}/functions/v1/eld-proxy`;

  const response = await fetch(
    proxyEndpoint,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        method,
        body,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API request failed: ${response.status}`);
  }

  return await response.json();
}

/**
 * Test ELD API connection for EVA ELD
 */
export async function testELDConnection(apiUrl: string, apiKey: string): Promise<{ 
  success: boolean; 
  error?: string 
}> {
  try {
    await supabase
      .from('system_settings')
      .upsert({
        setting_key: 'eld_api_key',
        setting_value: apiKey,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'setting_key'
      });

    await supabase
      .from('system_settings')
      .upsert({
        setting_key: 'eld_api_url',
        setting_value: apiUrl,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'setting_key'
      });

    await supabase
      .from('system_settings')
      .upsert({
        setting_key: 'eld_enabled',
        setting_value: 'true',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'setting_key'
      });

    await callELDAPI('/api/v0/locations');
    
    return { success: true };
  } catch (error: any) {
    console.error('Error testing ELD connection:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to connect to ELD API' 
    };
  }
}

/**
 * Test ProLogs API connection
 */
export async function testProLogsConnection(): Promise<{ 
  success: boolean; 
  error?: string;
  data?: any;
}> {
  try {
    const data = await callELDAPI('/api/v1/trucks');
    
    return { 
      success: true,
      data
    };
  } catch (error: any) {
    console.error('Error testing ProLogs connection:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to connect to ProLogs API' 
    };
  }
}

/**
 * Get vehicle locations from ELD API
 * This function adapts to different ELD provider API formats
 */
export async function getELDVehicleLocations(): Promise<{
  success: boolean;
  locations?: Array<{
    vehicleId: string;
    latitude: number;
    longitude: number;
    heading?: number;
    speed?: number;
    time: string;
  }>;
  error?: string;
}> {
  try {
    const provider = await getELDProvider();

    if (!provider) {
      return {
        success: false,
        error: 'ELD API not configured or not enabled',
      };
    }

    // For ProLogs, use the multi-connection service to get trucks from all accounts
    if (provider === 'PRO_LOGS') {
      const { getTrucksFromAllProLogsConnections } = await import('./eldConnectionsService');
      const result = await getTrucksFromAllProLogsConnections();
      
      if (!result.success || !result.trucks) {
        return {
          success: false,
          error: result.error || 'Failed to fetch trucks from ProLogs',
        };
      }

      // Convert ProLogs truck format to standard location format
      const locations = result.trucks.map((truck: any) => ({
        vehicleId: truck.vehicleId?.toString() || truck.name || 'unknown',
        name: truck.name, // Preserve truck name for matching with unit_number
        latitude: truck.lat || 0,
        longitude: truck.lng || 0,
        heading: truck.heading,
        speed: truck.speed || 0,
        time: truck.timeStamp || truck.time || new Date().toISOString(),
      }));

      return {
        success: true,
        locations,
      };
    }

    // For other providers (EVA ELD, etc.), use the legacy single-connection approach
    let endpoint = '/vehicles/locations';
    
    if (provider.toLowerCase().includes('eva')) {
      endpoint = '/api/v0/locations';
    }

    const data = await callELDAPI(endpoint);

    // Parse response based on provider
    const locations = parseELDLocations(data, provider);

    return {
      success: true,
      locations,
    };
  } catch (error: any) {
    console.error('Error fetching ELD vehicle locations:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch vehicle locations from ELD',
    };
  }
}

/**
 * Parse ELD API response based on provider format
 * Adapts different ELD provider formats to a common structure
 */
function parseELDLocations(data: any, provider: string): Array<{
  vehicleId: string;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  time: string;
}> {
  const locations: Array<{
    vehicleId: string;
    latitude: number;
    longitude: number;
    heading?: number;
    speed?: number;
    time: string;
  }> = [];

  // ProLogs API format
  if (provider === 'PRO_LOGS') {
    const trucks = Array.isArray(data) ? data : [];
    
    if (trucks.length === 0) {
      console.warn('No trucks returned from ProLogs API');
      return locations;
    }

    for (const truck of trucks) {
      try {
        if (!truck.lat || !truck.lng) {
          continue;
        }

        locations.push({
          vehicleId: truck.vehicleId?.toString() || truck.name || '',
          latitude: parseFloat(truck.lat),
          longitude: parseFloat(truck.lng),
          heading: undefined,
          speed: truck.speed || undefined,
          time: truck.timeStamp || new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error parsing ProLogs truck location:', error, truck);
      }
    }
    
    return locations;
  }
  
  // EVA ELD specific format
  if (provider.toLowerCase().includes('eva')) {
    const vehicles = data.vehicles || data.data || data;
    
    if (!Array.isArray(vehicles)) {
      console.warn('Unexpected EVA ELD API response format:', data);
      return locations;
    }

    for (const vehicle of vehicles) {
      try {
        // EVA ELD format: { id, vehicleId, locationTime, latitude, longitude, speed, bearing, etc }
        if (!vehicle.latitude || !vehicle.longitude) {
          continue;
        }

        locations.push({
          vehicleId: vehicle.vehicleId || vehicle.id || '',
          latitude: parseFloat(vehicle.latitude),
          longitude: parseFloat(vehicle.longitude),
          heading: vehicle.bearing ? parseFloat(vehicle.bearing) : undefined,
          speed: vehicle.speed || undefined,
          time: vehicle.locationTime ? new Date(vehicle.locationTime * 1000).toISOString() : new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error parsing EVA ELD vehicle location:', error, vehicle);
      }
    }
    
    return locations;
  }

  // Generic format for other ELD providers
  const vehicles = data.vehicles || data.data || data;

  if (!Array.isArray(vehicles)) {
    console.warn('Unexpected ELD API response format:', data);
    return locations;
  }

  for (const vehicle of vehicles) {
    try {
      // Common fields across most ELD providers
      const location = vehicle.location || vehicle.lastLocation || vehicle;
      
      if (!location.latitude || !location.longitude) {
        continue;
      }

      locations.push({
        vehicleId: vehicle.id || vehicle.vehicleId || vehicle.vehicle_id || '',
        latitude: parseFloat(location.latitude || location.lat),
        longitude: parseFloat(location.longitude || location.lng || location.lon),
        heading: location.heading || location.direction || undefined,
        speed: location.speed || location.speedMph || location.speed_mph || undefined,
        time: location.timestamp || location.time || vehicle.timestamp || new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error parsing vehicle location:', error, vehicle);
    }
  }

  return locations;
}

/**
 * Sync ELD vehicles to database
 */
export async function syncELDVehiclesToDatabase(): Promise<{
  success: boolean;
  added: number;
  updated: number;
  total: number;
  error?: string;
}> {
  try {
    const provider = await getELDProvider();

    if (!provider) {
      return {
        success: false,
        added: 0,
        updated: 0,
        total: 0,
        error: 'ELD API not configured',
      };
    }

    // Fetch vehicles from ELD API via Edge Function proxy
    const data = await callELDAPI('/vehicles');
    const vehicles = data.vehicles || data.data || data;

    if (!Array.isArray(vehicles)) {
      throw new Error('Invalid API response format');
    }

    let added = 0;
    let updated = 0;

    // Process each vehicle
    for (const vehicle of vehicles) {
      try {
        const vehicleId = vehicle.id || vehicle.vehicleId || vehicle.vehicle_id;
        const vehicleName = vehicle.name || vehicle.vehicleName || vehicle.vehicle_name || `Vehicle ${vehicleId}`;

        // Check if truck exists by eld_vehicle_id
        const { data: existingTruck, error: selectError } = await supabase
          .from('trucks')
          .select('id, eld_vehicle_id')
          .eq('eld_vehicle_id', vehicleId)
          .maybeSingle();

        const truckExists = existingTruck && !selectError;

        if (!truckExists) {
          // Create new truck
          const { error: insertError } = await supabase
            .from('trucks')
            .insert({
              unit_number: vehicleName,
              eld_vehicle_id: vehicleId,
              vin: vehicle.vin || null,
              make: vehicle.make || null,
              model: vehicle.model || null,
              year: vehicle.year ? parseInt(vehicle.year) : null,
              license_plate: vehicle.licensePlate || vehicle.license_plate || null,
              status: 'in_service',
            });

          if (!insertError) {
            added++;
          } else if (insertError.code !== '23505') {
            console.error(`Failed to add truck ${vehicleName}:`, insertError);
          }
        } else {
          // Update existing truck
          const { error: updateError } = await supabase
            .from('trucks')
            .update({
              unit_number: vehicleName,
              vin: vehicle.vin || null,
              make: vehicle.make || null,
              model: vehicle.model || null,
              year: vehicle.year ? parseInt(vehicle.year) : null,
              license_plate: vehicle.licensePlate || vehicle.license_plate || null,
            })
            .eq('id', existingTruck.id);

          if (!updateError) {
            updated++;
          }
        }
      } catch (vehicleError) {
        console.error('Error processing vehicle:', vehicleError);
      }
    }

    return {
      success: true,
      added,
      updated,
      total: vehicles.length,
    };
  } catch (error: any) {
    console.error('Error syncing ELD vehicles:', error);
    return {
      success: false,
      added: 0,
      updated: 0,
      total: 0,
      error: error.message,
    };
  }
}
